import { WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';
import { config, ROUTES } from '../app/config';

/**
 * Integracion OpenID Connect con Keycloak.
 *
 * - Authorization Code Flow + PKCE S256 (`response_type: 'code'` en un client
 *   publico activa PKCE en oidc-client-ts, que usa S256 por defecto).
 * - Los tokens NO se guardan en localStorage. Se usa `sessionStorage`, que se
 *   limpia al cerrar la pestana y no se comparte entre pestanas.
 * - La renovacion usa el refresh token contra Keycloak; no hay iframes ocultos
 *   ni trucos con cookies de terceros.
 * - El logout se hace SIEMPRE contra Keycloak (end_session_endpoint) para que
 *   caiga tambien la sesion SSO, no solo la local.
 */
export const oidcConfig: UserManagerSettings & { onSigninCallback?: () => void } = {
  authority: `${config.keycloakUrl}/realms/${config.keycloakRealm}`,
  client_id: config.keycloakClientId,
  redirect_uri: `${window.location.origin}${ROUTES.callback}`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  scope: 'openid profile email',

  // Renovacion silenciosa via refresh token, con margen antes de que expire.
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,

  // Evita dejar basura en el historial del navegador.
  monitorSession: false,
  loadUserInfo: false,

  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

  /**
   * Tras volver de Keycloak se limpia el `?code=...&state=...` de la barra de
   * direcciones para que un F5 no reintente canjear un codigo ya usado
   * (una de las causas clasicas de bucle de redirecciones).
   */
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

/**
 * Descarta el estado OIDC guardado cuando cambia el proveedor de identidad.
 *
 * `oidc-client-ts` deja en sessionStorage el usuario y los `state` de los
 * flujos en curso. Si el realm, la URL de Keycloak o el client cambian (un
 * renombrado, mover Keycloak de dominio, pasar de dev a produccion), ese
 * estado apunta a un sitio que ya no existe y el navegador acaba en un
 * "Page not found" de Keycloak hasta que alguien limpia a mano.
 *
 * Aqui se guarda la identidad del proveedor actual: si no coincide con la de
 * la ultima visita, se borra todo lo de `oidc.` y se empieza limpio.
 */
const IDENTITY_KEY = 'ee.identityProvider';

export function discardStaleOidcState(): void {
  const current = `${config.keycloakUrl}|${config.keycloakRealm}|${config.keycloakClientId}`;

  try {
    const previous = window.sessionStorage.getItem(IDENTITY_KEY);
    if (previous === current) return;

    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith('oidc.')) {
        window.sessionStorage.removeItem(key);
      }
    }
    window.sessionStorage.setItem(IDENTITY_KEY, current);
  } catch {
    // sessionStorage bloqueado (modo privado): no es critico.
  }
}

/**
 * Cuando se intento por ultima vez rescatar una sesion caducada.
 *
 * Existe para que ese rescate ocurra UNA vez. Si al volver de Keycloak la
 * sesion sigue sin valer, repetir el viaje seria un bucle de redirecciones que
 * el usuario no puede parar: pasado el margen se le ensena el error, que es
 * feo pero tiene salida.
 */
const REAUTH_KEY = 'ee.reauthAt';
const MARGEN_REAUTENTICACION_MS = 20_000;

export function reautenticacionReciente(): boolean {
  try {
    const previo = Number(window.sessionStorage.getItem(REAUTH_KEY));
    return Number.isFinite(previo) && previo > 0 && Date.now() - previo < MARGEN_REAUTENTICACION_MS;
  } catch {
    return false;
  }
}

export function anotarReautenticacion(): void {
  try {
    window.sessionStorage.setItem(REAUTH_KEY, String(Date.now()));
  } catch {
    // sessionStorage bloqueado (modo privado): no es critico.
  }
}

/** Tras entrar bien, la proxima caducidad vuelve a tener derecho a su intento. */
export function olvidarReautenticacion(): void {
  try {
    window.sessionStorage.removeItem(REAUTH_KEY);
  } catch {
    // Igual que arriba.
  }
}

/** Clave donde se recuerda la ruta a la que el usuario queria entrar. */
export const RETURN_TO_KEY = 'ee.returnTo';

export function rememberReturnTo(path: string): void {
  try {
    if (path && path !== ROUTES.callback && path !== ROUTES.login) {
      window.sessionStorage.setItem(RETURN_TO_KEY, path);
    }
  } catch {
    // sessionStorage puede estar bloqueado; no es critico.
  }
}

export function consumeReturnTo(): string | null {
  try {
    const value = window.sessionStorage.getItem(RETURN_TO_KEY);
    window.sessionStorage.removeItem(RETURN_TO_KEY);
    return value;
  } catch {
    return null;
  }
}
